import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { useAuth } from '../../contexts/AuthContext';
import { useHouseholdMembership } from '../../contexts/HouseholdMembershipContext';
import { navigateToAuth } from '../../core/navigation/navigationRef';
import { clearPendingInviteToken, rememberDismissedInviteToken } from '../../core/storage/inviteTokenStorage';
import { colors, spacing, typography } from '../../core/theme/tokens';
import { ApiError } from '../../services/apiClient';
import { forcePullDashboards } from '../../services/analyticsService';
import {
  acceptHouseholdInvite,
  lookupHouseholdInvite,
  type HouseholdInviteLookup,
} from '../../services/householdService';
import { notifyNotificationsChanged } from '../../services/notificationsBus';
import { GlassButton } from '../ui/GlassButton';
import { GlassCard } from '../ui/GlassCard';

type Props = {
  visible: boolean;
  token: string | null;
  onClose: () => void;
  onJoined: () => void;
};

export function JoinInviteModal({ visible, token, onClose, onJoined }: Props) {
  const { isAuthenticated, refreshUser } = useAuth();
  const { refresh, applyHousehold } = useHouseholdMembership();
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<HouseholdInviteLookup['invite'] | null>(null);

  const loadPreview = useCallback(async (inviteToken: string) => {
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const res = await lookupHouseholdInvite(inviteToken);
      if (!res.valid || !res.invite) {
        setError(res.error || 'Invite is invalid, expired, or revoked');
        return;
      }
      setPreview(res.invite);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not load invite',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!visible || !token) return;
    void loadPreview(token);
  }, [visible, token, loadPreview]);

  const dismiss = async (clearToken: boolean) => {
    if (clearToken) {
      if (token) await rememberDismissedInviteToken(token);
      await clearPendingInviteToken();
    }
    notifyNotificationsChanged();
    onClose();
  };

  const onPrimary = async () => {
    if (!token) return;

    if (!isAuthenticated) {
      navigateToAuth();
      return;
    }

    setJoining(true);
    setError(null);
    try {
      const res = await acceptHouseholdInvite(token);
      await clearPendingInviteToken();
      if (res.household) applyHousehold(res.household);
      await refreshUser();
      await refresh();
      await forcePullDashboards({ reason: 'household_joined' });
      onJoined();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 401) {
        navigateToAuth();
        return;
      }
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not join household',
      );
    } finally {
      setJoining(false);
    }
  };

  const inviter = preview?.invitedByName || 'Someone';
  const householdLabel = preview?.householdName?.trim() || 'their household';

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={() => void dismiss(false)}>
      <Pressable style={styles.backdrop} onPress={() => void dismiss(false)}>
        <Pressable style={styles.sheet} onPress={(e) => e.stopPropagation()}>
          <GlassCard>
            <Text style={styles.eyebrow}>MoneyLens</Text>
            <Text style={styles.title}>Join household</Text>

            {loading ? (
              <ActivityIndicator color={colors.accent} style={{ marginVertical: spacing.lg }} />
            ) : error && !preview ? (
              <Text style={styles.error}>{error}</Text>
            ) : (
              <>
                <View style={styles.inviterRow}>
                  {preview?.invitedByPicture ? (
                    <Image source={{ uri: preview.invitedByPicture }} style={styles.avatar} />
                  ) : (
                    <View style={[styles.avatar, styles.avatarFallback]}>
                      <Text style={styles.avatarLetter}>
                        {inviter.slice(0, 1).toUpperCase()}
                      </Text>
                    </View>
                  )}
                  <View style={styles.inviterCopy}>
                    <Text style={styles.inviterName}>{inviter}</Text>
                    <Text style={styles.sub}>
                      invited you to {householdLabel}. Keep a private Mine view; Household shows
                      what you both share.
                    </Text>
                  </View>
                </View>

                {preview?.expiresAt ? (
                  <Text style={styles.meta}>
                    Invite expires {new Date(preview.expiresAt).toLocaleDateString()}
                  </Text>
                ) : null}
              </>
            )}

            {error && preview ? <Text style={styles.error}>{error}</Text> : null}

            {!loading && preview ? (
              <>
                <GlassButton
                  variant="primary"
                  fullWidth
                  disabled={joining}
                  onPress={() => void onPrimary()}
                  style={{ marginTop: spacing.md }}
                >
                  {joining ? (
                    <ActivityIndicator color={colors.text} />
                  ) : isAuthenticated ? (
                    'Join household'
                  ) : (
                    'Sign in to join'
                  )}
                </GlassButton>
                {!isAuthenticated ? (
                  <Text style={styles.hint}>
                    New here? Create an account — you’ll return here to join.
                  </Text>
                ) : null}
              </>
            ) : null}

            <GlassButton
              variant="ghost"
              fullWidth
              onPress={() => void dismiss(true)}
              style={{ marginTop: spacing.sm }}
            >
              Later
            </GlassButton>
            <Text style={styles.hint}>Find this invite later in Notifications.</Text>
          </GlassCard>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(2, 6, 23, 0.72)',
    justifyContent: 'center',
    padding: spacing.lg,
  },
  sheet: { maxWidth: 440, width: '100%', alignSelf: 'center' },
  eyebrow: {
    ...typography.caption,
    color: colors.accent,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  title: { ...typography.headline, fontSize: 22, color: colors.text, marginTop: spacing.sm },
  inviterRow: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.md },
  avatar: { width: 48, height: 48, borderRadius: 24 },
  avatarFallback: {
    backgroundColor: 'rgba(61, 214, 164, 0.18)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.35)',
  },
  avatarLetter: { ...typography.headline, fontSize: 18, color: colors.accent },
  inviterCopy: { flex: 1, gap: 4 },
  inviterName: { ...typography.headline, fontSize: 16, color: colors.text },
  sub: { ...typography.body, fontSize: 14, color: colors.textMuted, lineHeight: 20 },
  meta: { ...typography.caption, color: colors.textMuted, marginTop: spacing.md },
  error: { ...typography.caption, color: '#F87171', marginTop: spacing.md },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
    marginTop: spacing.sm,
    textAlign: 'center',
  },
});
